import React from 'react';
import { sanitizeHtml } from './sanitizeHtml';
import { sanitizeUrl } from './sanitize';

/**
 * Extracts <style> tags from HTML and returns them separately
 * This allows CSS styles embedded in code blocks to be applied properly
 */
const extractStylesFromHtml = (rawHtml) => {
  // Sanitize CMS-authored HTML before it reaches dangerouslySetInnerHTML below.
  // Strips <script>/event-handlers/javascript: while keeping the inline-<style> feature.
  const html = sanitizeHtml(rawHtml || '');
  const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
  const styles = [];
  let match;

  while ((match = styleRegex.exec(html)) !== null) {
    styles.push(match[0]);
  }

  // Remove style tags from the HTML content
  const htmlWithoutStyles = html.replace(styleRegex, '');

  return { styles, htmlWithoutStyles };
};

/**
 * Renders HTML content with embedded CSS styles properly
 * Extracts <style> tags and renders them separately so they can affect the document
 */
const RenderHtmlWithStyles = ({ html, className }) => {
  const { styles, htmlWithoutStyles } = extractStylesFromHtml(html);

  return (
    <>
      {styles.map((style, index) => (
        <span key={`style-${index}`} dangerouslySetInnerHTML={{ __html: style }} />
      ))}
      {htmlWithoutStyles.trim() && (
        <span className={className} dangerouslySetInnerHTML={{ __html: htmlWithoutStyles }} />
      )}
    </>
  );
};

/**
 * Renders a block-level HTML content with embedded CSS styles
 */
const RenderHtmlBlockWithStyles = ({ html }) => {
  const { styles, htmlWithoutStyles } = extractStylesFromHtml(html);

  return (
    <div>
      {styles.map((style, index) => (
        <style key={`style-${index}`} dangerouslySetInnerHTML={{ __html: style.replace(/<\/?style[^>]*>/gi, '') }} />
      ))}
      {htmlWithoutStyles.trim() && (
        <div dangerouslySetInnerHTML={{ __html: htmlWithoutStyles }} />
      )}
    </div>
  );
};

/**
 * Converts Strapi rich text format to React elements
 * Handles nested structures, formatting (bold, italic, underline, strikethrough, code), and links
 */
export const convertStrapiRichTextToReact = (content) => {
  if (!content) return null;
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return null;

  // Helper to check if a block contains only code nodes
  const isCodeBlock = (block) => {
    if (!block.children || !Array.isArray(block.children)) return false;
    return block.children.every(child =>
      child.code === true || child.text === ''
    );
  };

  // Helper to extract text from a block's children
  const extractTextFromBlock = (block) => {
    if (!block.children || !Array.isArray(block.children)) return '';
    return block.children
      .filter(child => child.text !== '')
      .map(child => child.text)
      .join('\n');
  };

  // First pass: merge consecutive code blocks into single HTML blocks
  const mergedBlocks = [];
  let codeBuffer = [];

  for (let i = 0; i < content.length; i++) {
    const block = content[i];

    if (isCodeBlock(block)) {
      // Accumulate code from this block
      const text = extractTextFromBlock(block);
      if (text) {
        codeBuffer.push(text);
      }
    } else {
      // If we have accumulated code, flush it as a single HTML block
      if (codeBuffer.length > 0) {
        mergedBlocks.push({
          type: 'html',
          html: codeBuffer.join('\n'),
          _merged: true
        });
        codeBuffer = [];
      }
      // Add the non-code block
      mergedBlocks.push(block);
    }
  }

  // Flush any remaining code
  if (codeBuffer.length > 0) {
    mergedBlocks.push({
      type: 'html',
      html: codeBuffer.join('\n'),
      _merged: true
    });
  }

  const processChildren = (children, parentKey = '') => {
    if (!Array.isArray(children)) return null;

    // Also handle code merging within children for inline cases
    const mergedChildren = [];
    let codeBuffer = [];

    for (let i = 0; i < children.length; i++) {
      const child = children[i];

      if (child.code && child.text !== undefined) {
        // Accumulate code text
        codeBuffer.push(child.text);
      } else {
        // If we have accumulated code, flush it
        if (codeBuffer.length > 0) {
          mergedChildren.push({
            code: true,
            text: codeBuffer.join('\n'),
            _merged: true
          });
          codeBuffer = [];
        }
        // Add the non-code child
        mergedChildren.push(child);
      }
    }

    // Flush any remaining code
    if (codeBuffer.length > 0) {
      mergedChildren.push({
        code: true,
        text: codeBuffer.join('\n'),
        _merged: true
      });
    }

    return mergedChildren.map((child, index) => {
      const key = `${parentKey}-${index}`;

      // If text is empty string, it's a line break
      if (child.text === '') {
        return <br key={key} />;
      }

      // Handle link blocks (links have children with the text inside)
      if (child.type === 'link' && child.url) {
        // Process children to handle formatting within links
        const linkChildren = child.children ? child.children.map((linkChild, linkIndex) => {
          const linkKey = `${key}-link-${linkIndex}`;

          if (linkChild.text === '') {
            return <br key={linkKey} />;
          }

          if (linkChild.text) {
            let element = linkChild.text;

            // Apply formatting to text within links
            if (linkChild.code) {
              // Render inline code as HTML with CSS support
              element = <RenderHtmlWithStyles key={linkKey} html={element} />;
            } else {
              if (linkChild.bold) {
                element = <strong key={linkKey}>{element}</strong>;
              }
              if (linkChild.italic) {
                element = <em key={linkKey}>{element}</em>;
              }
              if (linkChild.underline) {
                element = <u key={linkKey}>{element}</u>;
              }
              if (linkChild.strikethrough) {
                element = <s key={linkKey}>{element}</s>;
              }
            }

            return element;
          }

          return null;
        }).filter(Boolean) : null;

        return (
          <a key={key} href={sanitizeUrl(child.url)} target="_blank" rel="noopener noreferrer" >
            {linkChildren}
          </a>
        );
      }

      // If child has nested children (but not a link), process them recursively
      if (child.children && Array.isArray(child.children)) {
        return processChildren(child.children, key);
      }

      // Process text with formatting
      if (child.text) {
        let element = child.text;

        // Apply formatting in order (innermost to outermost)
        if (child.code) {
          // Render inline code as HTML with CSS support
          element = <RenderHtmlWithStyles key={key} html={element} />;
        } else {
          if (child.bold) {
            element = <strong key={key}>{element}</strong>;
          }
          if (child.italic) {
            element = <em key={key}>{element}</em>;
          }
          if (child.underline) {
            element = <u key={key}>{element}</u>;
          }
          if (child.strikethrough) {
            element = <s key={key}>{element}</s>;
          }
        }

        return element;
      }

      return null;
    }).filter(Boolean);
  };

  const processBlocks = (blocks) => {
    return blocks.map((block, index) => {
      const key = `block-${index}`;

      // Handle merged HTML blocks with CSS support
      if (block.type === 'html' && block.html) {
        return <RenderHtmlBlockWithStyles key={key} html={block.html} />;
      }

      if (block.type === 'paragraph') {
        const children = processChildren(block.children || [], key);
        return <p key={key}>{children}</p>;
      }

      if (block.type === 'heading') {
        const level = block.level || 1;
        const HeadingTag = `h${level}`;
        const children = processChildren(block.children || [], key);
        return React.createElement(HeadingTag, { key }, children);
      }

      if (block.type === 'list') {
        const ListTag = block.format === 'ordered' ? 'ol' : 'ul';
        // List children are list-item blocks, so we need to process them as blocks
        const children = block.children ? block.children.map((item, itemIndex) => {
          const itemKey = `${key}-item-${itemIndex}`;
          if (item.type === 'list-item') {
            const itemChildren = processChildren(item.children || [], itemKey);
            return <li key={itemKey}>{itemChildren}</li>;
          }
          return null;
        }).filter(Boolean) : null;
        return React.createElement(ListTag, { key }, children);
      }

      if (block.type === 'list-item') {
        const children = processChildren(block.children || [], key);
        return <li key={key}>{children}</li>;
      }

      if (block.type === 'quote') {
        const children = processChildren(block.children || [], key);
        return <blockquote key={key}>{children}</blockquote>;
      }

      if (block.type === 'code') {
        const children = processChildren(block.children || [], key);
        return <pre key={key}><code>{children}</code></pre>;
      }

      if (block.type === 'link') {
        const children = processChildren(block.children || [], key);
        return (
          <a key={key} href={sanitizeUrl(block.url)} target="_blank" rel="noopener noreferrer">
            {children}
          </a>
        );
      }

      // Default: treat as paragraph if it has children
      if (block.children) {
        const children = processChildren(block.children || [], key);
        return <p key={key}>{children}</p>;
      }

      return null;
    }).filter(Boolean);
  };

  return <div>{processBlocks(mergedBlocks)}</div>;
};

/**
 * Extracts plain text from Strapi rich text format for meta descriptions
 */
export const extractPlainTextFromStrapiRichText = (content) => {
  if (!content) return '';
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';

  const extractTextFromChildren = (children) => {
    if (!Array.isArray(children)) return '';
    
    return children
      .map(child => {
        if (child.text === '') return '\n';
        
        // Handle link blocks - extract text from link children
        if (child.type === 'link' && child.children && Array.isArray(child.children)) {
          return extractTextFromChildren(child.children);
        }
        
        // Handle nested children
        if (child.children && Array.isArray(child.children)) {
          return extractTextFromChildren(child.children);
        }
        
        return child.text || '';
      })
      .join('');
  };

  return content
    .map(block => {
      if (block.children && Array.isArray(block.children)) {
        return extractTextFromChildren(block.children);
      }
      return '';
    })
    .join('\n')
    .trim();
};